// BYOPWorkflow.js
//
// Detail + launch page for an imported "bring your own pipeline" (BYOP) public
// Nextflow workflow. Mirrors WorkflowDetails.js (same CSS + section layout +
// NFViz diagram applet), with two BYOP-specific differences:
//   * PARAMETERS are typed input fields (number/text/checkbox/enum/list) with
//     defaults pre-filled — public pipelines are configured on the command line.
//   * INPUT FILES are chosen from the user's existing file LIBRARY: a searchable,
//     paginated, sortable table whose rows are DRAGGED into per-input drop
//     sections (no uploading here — files must already be in the library).
//
// On launch, the selected file IDs per input + parameter values are sent to
// /byop/launch-analysis, which copies those library files into the run and
// assembles the `nextflow run … --flag value` command.
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Modal from "react-modal";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import "./css/FileUpload.css";
import "./css/WorkflowDetails.css";
import API_BASE from "./config";
import Pagination from "./components/Pagination";
import { normalizePrograms } from "./utils/normalizePrograms";

const FILES_PER_PAGE = 15;

const BYOPWorkflow = ({ chatId, cognitoUserId, organization }) => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    const [paramValues, setParamValues] = useState({});
    const [assigned, setAssigned] = useState({}); // { inputName: [{id, name}] }
    const [showValidation, setShowValidation] = useState(false); // reveal missing-item highlights after a blocked launch
    const [modalOpen, setModalOpen] = useState(false);
    const [launchResult, setLaunchResult] = useState("");
    const [savingParams, setSavingParams] = useState(false);
    const [paramNotice, setParamNotice] = useState("");

    // user file library
    const [userFiles, setUserFiles] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [currentPage, setCurrentPage] = useState(1);
    const [selected, setSelected] = useState({}); // { fileId: true } for multi-drag

    // source-code editor
    const [fileTree, setFileTree] = useState([]);
    const [editorFile, setEditorFile] = useState(null); // { path, content, editable, isDockerfile, readOnly }
    const [editorSaving, setEditorSaving] = useState(false);
    const [codeNotice, setCodeNotice] = useState("");
    const [expanded, setExpanded] = useState({});       // { dirPath: true } — folders collapsed by default
    const [auditRows, setAuditRows] = useState(null);   // null = not loaded yet

    const refreshTree = React.useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE}/byop-files/${chatId}`, { withCredentials: true });
            setFileTree(res.data.files || []);
        } catch (_) { /* pipeline may have no on-disk source */ }
    }, [chatId]);

    useEffect(() => { refreshTree(); }, [refreshTree]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API_BASE}/byop-workflow/${chatId}`);
                if (cancelled) return;
                setData(res.data);
                const init = {};
                (res.data.parameters || []).forEach((p) => {
                    if (p.type === "boolean") init[p.name] = p.default === "true" || p.default === true;
                    else if (p.type === "list") init[p.name] = p.default ? String(p.default).split(",").map((s) => s.trim()).filter(Boolean) : [];
                    else init[p.name] = p.default ?? "";
                });
                setParamValues(init);
            } catch (e) {
                if (!cancelled) setError("Could not load this pipeline.");
            }
        })();
        return () => { cancelled = true; };
    }, [chatId]);

    // Fetch the user's file library (same source as LaunchBuild).
    useEffect(() => {
        if (!cognitoUserId) return;
        (async () => {
            try {
                const idToken = localStorage.getItem("idToken");
                const res = await fetch(`${API_BASE}/user-files`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
                    body: JSON.stringify({ cognitoUserId }),
                });
                const d = await res.json();
                setUserFiles(
                    (d.files || []).map((f) => ({
                        id: f.id,
                        name: f.filename,
                        project: f.original_project || "",
                        path: f.s3_folder_path || "",
                        created: f.created_on
                            ? new Date(f.created_on).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
                            : "",
                    }))
                );
            } catch (e) {
                console.error("Error fetching user files:", e);
            }
        })();
    }, [cognitoUserId]);

    // --- file library: sort + search (all columns) + paginate ---
    const sortedFiles = useMemo(() => {
        if (!sortConfig.key) return userFiles;
        return [...userFiles].sort((a, b) => {
            const av = (a[sortConfig.key] || "").toString().toLowerCase();
            const bv = (b[sortConfig.key] || "").toString().toLowerCase();
            if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
            if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [userFiles, sortConfig]);

    const filteredFiles = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return sortedFiles;
        return sortedFiles.filter((f) =>
            ["name", "project", "path", "created"].some((k) => String(f[k] || "").toLowerCase().includes(q))
        );
    }, [sortedFiles, searchQuery]);

    const totalPages = Math.max(1, Math.ceil(filteredFiles.length / FILES_PER_PAGE));
    const page = Math.min(currentPage, totalPages);
    const pagedFiles = filteredFiles.slice((page - 1) * FILES_PER_PAGE, page * FILES_PER_PAGE);

    if (error) return <p className="workflow-error-text">{error}</p>;
    if (!data) return <p className="workflow-loading-text">Loading pipeline…</p>;

    const inputs = Array.isArray(data.input_files) ? data.input_files : [];
    const params = Array.isArray(data.parameters) ? data.parameters : [];

    // ---- drag & drop assignment ----
    const handleSort = (key) =>
        setSortConfig((prev) => (prev.key === key ? { key, direction: prev.direction === "asc" ? "desc" : "asc" } : { key, direction: "asc" }));

    const assignFiles = (inputName, files) =>
        setAssigned((prev) => {
            const cur = prev[inputName] || [];
            const seen = new Set(cur.map((f) => f.id));
            const merged = [...cur];
            files.forEach((f) => { if (f && f.id != null && !seen.has(f.id)) { merged.push(f); seen.add(f.id); } });
            return { ...prev, [inputName]: merged };
        });

    const removeFile = (inputName, id) =>
        setAssigned((prev) => ({ ...prev, [inputName]: (prev[inputName] || []).filter((f) => f.id !== id) }));

    const toggleSelect = (id) =>
        setSelected((prev) => {
            const n = { ...prev };
            if (n[id]) delete n[id]; else n[id] = true;
            return n;
        });
    const pageAllSelected = pagedFiles.length > 0 && pagedFiles.every((f) => selected[f.id]);
    const toggleSelectPage = () =>
        setSelected((prev) => {
            const n = { ...prev };
            if (pageAllSelected) pagedFiles.forEach((f) => delete n[f.id]);
            else pagedFiles.forEach((f) => { n[f.id] = true; });
            return n;
        });

    // Dragging a SELECTED row drags the whole selection; an unselected row drags itself.
    const onRowDragStart = (e, file) => {
        const payload = selected[file.id]
            ? userFiles.filter((f) => selected[f.id]).map((f) => ({ id: f.id, name: f.name }))
            : [{ id: file.id, name: file.name }];
        e.dataTransfer.setData("application/json", JSON.stringify(payload));
    };
    const onSectionDrop = (e, inputName) => {
        e.preventDefault();
        try {
            const arr = JSON.parse(e.dataTransfer.getData("application/json"));
            assignFiles(inputName, Array.isArray(arr) ? arr : [arr]);
        } catch (_) { /* ignore */ }
    };

    const SortHeader = ({ title, k }) => {
        let icon = <FaSort style={{ marginLeft: 6 }} />;
        if (sortConfig.key === k) icon = sortConfig.direction === "asc" ? <FaSortUp style={{ marginLeft: 6 }} /> : <FaSortDown style={{ marginLeft: 6 }} />;
        return (
            <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }} onClick={() => handleSort(k)}>
                {title}{icon}
            </span>
        );
    };

    // ---- typed parameter fields ----
    const setParam = (name, value) => setParamValues((prev) => ({ ...prev, [name]: value }));

    // Persist the current parameter values as the pipeline's stored defaults (so an edit
    // made here sticks for future launches). The values are already used for THIS launch.
    const saveParamDefaults = async () => {
        setSavingParams(true); setParamNotice("");
        try {
            await axios.post(`${API_BASE}/byop/params/${chatId}`, { paramValues });
            setParamNotice("✓ Saved as the pipeline's default parameter values.");
        } catch (e) {
            setParamNotice("Could not save parameter values.");
        } finally { setSavingParams(false); }
    };
    const renderParamField = (p) => {
        const val = paramValues[p.name];
        if (p.type === "boolean")
            return <input type="checkbox" checked={val === true || val === "true"} onChange={(e) => setParam(p.name, e.target.checked)} />;
        if (p.type === "enum") {
            // Options = declared choices PLUS the default + current value, so the default is
            // always selectable/pre-selected even if a pipeline was saved without options.
            const opts = Array.from(new Set([
                ...(p.choices || []).map(String),
                ...(p.default != null && p.default !== "" ? [String(p.default)] : []),
                ...(val != null && val !== "" ? [String(val)] : []),
            ]));
            return (
                <select value={val ?? ""} onChange={(e) => setParam(p.name, e.target.value)} style={fieldStyle}>
                    <option value="">-- Select --</option>
                    {opts.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
            );
        }
        if (p.type === "list") {
            const selected = Array.isArray(val) ? val : (val ? String(val).split(",").map((s) => s.trim()).filter(Boolean) : []);
            const defToks = p.default ? String(p.default).split(",").map((s) => s.trim()).filter(Boolean) : [];
            const opts = Array.from(new Set([...(p.choices || []).map(String), ...defToks, ...selected.map(String)]));
            if (opts.length) {
                return (
                    <select multiple value={selected} onChange={(e) => setParam(p.name, Array.from(e.target.selectedOptions).map((o) => o.value))} style={{ ...fieldStyle, minHeight: 72 }}>
                        {opts.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                );
            }
            return <input type="text" value={selected.join(",")} placeholder="comma,separated,values" onChange={(e) => setParam(p.name, e.target.value)} style={fieldStyle} />;
        }
        if (p.type === "number")
            return <input type="number" value={val ?? ""} onChange={(e) => setParam(p.name, e.target.value)} style={fieldStyle} />;
        return (
            <input
                type="text"
                value={val ?? ""}
                placeholder={p.type === "list" ? "comma,separated,values" : p.type === "path" ? "https://… or s3://…" : ""}
                onChange={(e) => setParam(p.name, e.target.value)}
                style={fieldStyle}
            />
        );
    };

    // ---- source-code editing ----
    const openFile = async (filePath) => {
        setCodeNotice("");
        try {
            const res = await axios.get(`${API_BASE}/byop-file/${chatId}`, { params: { path: filePath }, withCredentials: true });
            setEditorFile({ path: filePath, content: res.data.content || "", editable: res.data.editable !== false, isDockerfile: false });
        } catch (e) {
            setCodeNotice("Could not open file: " + (e.response?.data?.error || e.message));
        }
    };

    const saveEditorFile = async () => {
        if (!editorFile) return;
        setEditorSaving(true);
        try {
            await axios.post(`${API_BASE}/byop-file/${chatId}`, { path: editorFile.path, content: editorFile.content }, { withCredentials: true });
            setCodeNotice(`Saved ${editorFile.path}. Your edit will be used on the next launch.`);
            refreshTree();
            setEditorFile(null);
        } catch (e) {
            setCodeNotice("Save failed: " + (e.response?.data?.error || e.message));
        } finally {
            setEditorSaving(false);
        }
    };

    const deleteFile = async (filePath) => {
        if (!window.confirm(`Delete ${filePath}? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API_BASE}/byop-file/${chatId}`, { params: { path: filePath }, withCredentials: true });
            setCodeNotice(`Deleted ${filePath}.`);
            refreshTree();
        } catch (e) {
            setCodeNotice("Delete failed: " + (e.response?.data?.error || e.message));
        }
    };

    const addFile = () => {
        const p = window.prompt("New file path (relative to the pipeline root), e.g. modules/local/mytool.nf");
        if (!p) return;
        setEditorFile({ path: p.replace(/^\/+/, ""), content: "", editable: true, isDockerfile: false });
    };

    const renameFile = async (fromPath) => {
        const to = window.prompt("Rename / move to (path relative to the pipeline root):", fromPath);
        if (!to || to === fromPath) return;
        try {
            await axios.post(`${API_BASE}/byop-file-rename/${chatId}`, { from: fromPath, to: to.replace(/^\/+/, "") }, { withCredentials: true });
            setCodeNotice(`Renamed ${fromPath} → ${to}.`);
            refreshTree();
        } catch (e) {
            setCodeNotice("Rename failed: " + (e.response?.data?.error || e.message));
        }
    };

    const toggleDir = (p) => setExpanded((prev) => ({ ...prev, [p]: !prev[p] }));

    const loadAudit = async () => {
        try {
            const res = await axios.get(`${API_BASE}/byop-audit/${chatId}`, { withCredentials: true });
            setAuditRows(res.data.audit || []);
        } catch (e) {
            setCodeNotice("Could not load change history: " + (e.response?.data?.error || e.message));
            setAuditRows([]);
        }
    };

    // View the previous version captured in an audit entry (read-only).
    const viewAuditEntry = async (entryId) => {
        try {
            const res = await axios.get(`${API_BASE}/byop-audit/${chatId}/${entryId}`, { withCredentials: true });
            setEditorFile({
                path: `${res.data.file_path || "file"} — previous version (${new Date(res.data.created_at).toLocaleString()})`,
                content: res.data.prev_content || "(no previous content captured)",
                editable: false,
                isDockerfile: false,
                readOnly: true,
            });
        } catch (e) {
            setCodeNotice("Could not load previous version: " + (e.response?.data?.error || e.message));
        }
    };

    // Recursive file-tree renderer (folders first, collapsible).
    const renderTreeNode = (node, parentPath = "", depth = 0) => {
        const names = Object.keys(node).sort((a, b) => {
            const ad = node[a].__meta ? node[a].__meta.type === "dir" : true;
            const bd = node[b].__meta ? node[b].__meta.type === "dir" : true;
            if (ad !== bd) return ad ? -1 : 1; // folders first
            return a.localeCompare(b);
        });
        return names.map((name) => {
            const entry = node[name];
            const meta = entry.__meta;
            const fullPath = parentPath ? `${parentPath}/${name}` : name;
            const isDir = meta ? meta.type === "dir" : Object.keys(entry.__children).length > 0;
            if (isDir) {
                const isExpanded = expanded[fullPath];
                return (
                    <div key={fullPath}>
                        <div onClick={() => toggleDir(fullPath)} style={{ paddingLeft: depth * 16, cursor: "pointer", fontSize: 13, paddingTop: 2, paddingBottom: 2 }}>
                            <span style={{ display: "inline-block", width: 14 }}>{isExpanded ? "▾" : "▸"}</span>📁 {name}
                        </div>
                        {isExpanded && renderTreeNode(entry.__children, fullPath, depth + 1)}
                    </div>
                );
            }
            const locked = meta && meta.editable === false;
            return (
                <div key={fullPath} style={{ paddingLeft: depth * 16 + 14, display: "flex", alignItems: "center", gap: 8, paddingTop: 2, paddingBottom: 2 }}>
                    <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                        📄 {name}{locked ? <span style={{ color: "#94a3b8" }}> (locked)</span> : null}
                    </span>
                    <button style={linkBtn} onClick={() => openFile(fullPath)}>{locked ? "View" : "Edit"}</button>
                    {!locked && <button style={linkBtn} onClick={() => renameFile(fullPath)}>Rename</button>}
                    {!locked && <button style={{ ...linkBtn, color: "#b91c1c" }} onClick={() => deleteFile(fullPath)}>Delete</button>}
                </div>
            );
        });
    };

    // ---- launch gating ----
    const validated = data.validated_final === "Y";
    // What's still missing (computed live so highlights clear as the user fills them).
    // Only REQUIRED inputs/params block launch; optional ones may be left empty.
    const missingInputNames = inputs.filter((f) => f.required !== false && !((assigned[f.name] || []).length > 0)).map((f) => f.name);
    const missingParamNames = params
        .filter((p) => p.required && p.type !== "boolean" && (paramValues[p.name] === "" || paramValues[p.name] == null))
        .map((p) => p.name);
    // Path params that have a value must be a valid http(s):// or s3:// URL.
    const isPathOk = (v) => { v = String(v || "").trim(); return /^https?:\/\/.+/i.test(v) || /^s3:\/\/[^/\s]+(\/.*)?$/i.test(v); };
    const invalidParamNames = params
        .filter((p) => p.type === "path" && paramValues[p.name] != null && String(paramValues[p.name]).trim() !== "" && !isPathOk(paramValues[p.name]))
        .map((p) => p.name);
    const canLaunch = validated && missingInputNames.length === 0 && missingParamNames.length === 0 && invalidParamNames.length === 0;

    // Live preview of the exact command that will run — mirrors the server's
    // /byop/launch-analysis assembly: inputs get the selected filenames; a parameter is
    // added only when its value is non-empty AND changed from the default (boolean = flag
    // when on). Shown (no submission) once everything required is selected.
    const previewCommand = (() => {
        const main = String(data.main_script || data.nextflow_path || "main.nf").split("/").pop();
        const parts = [`nextflow run ${main}`];
        inputs.forEach((f) => {
            const names = (assigned[f.name] || []).map((x) => x.name);
            if (names.length) parts.push(`${f.flag} ${names.join(",")}`);
        });
        params.forEach((p) => {
            const v = paramValues[p.name];
            const always = !!p.alwaysInclude; // "Add to Command" → force inclusion
            if (p.type === "boolean") { if (always || v === true || v === "true") parts.push(p.flag); return; }
            const sv = Array.isArray(v) ? v.join(",") : (v == null ? "" : String(v));
            if (sv.trim() === "") return;
            if (!always && p.default != null && String(p.default) === sv) return; // unchanged from default → pipeline uses its typed default
            parts.push(`${p.flag} ${/\s/.test(sv) ? `"${sv}"` : sv}`);
        });
        return parts.join(" ");
    })();

    const openLaunch = () => {
        if (!validated) return; // button is disabled in this case anyway
        if (missingInputNames.length || missingParamNames.length || invalidParamNames.length) {
            setShowValidation(true); // reveal highlights + the missing/invalid-items list
            return;
        }
        setShowValidation(false);
        setModalOpen(true);
    };

    const confirmLaunch = async () => {
        try {
            const inputFileIds = {};
            inputs.forEach((f) => { inputFileIds[f.name] = (assigned[f.name] || []).map((x) => x.id); });
            // Static "Data" input (every pipeline): raw data files to copy into the run
            // folder (so samplesheets/scripts find them by name) — NOT passed as a flag.
            // The reserved "__data__" key matches no declared input, so the backend copies
            // these files (allIds union) without adding a command flag.
            const dataIds = (assigned["__data__"] || []).map((x) => x.id);
            if (dataIds.length) inputFileIds["__data__"] = dataIds;
            await axios.post(
                `${API_BASE}/byop/launch-analysis`,
                { chatId, paramValues, inputFileIds },
                { withCredentials: true }
            );
            // Launch accepted — take the user straight to Analyses to watch the run.
            setModalOpen(false);
            navigate("/analyses");
        } catch (e) {
            setLaunchResult("Launch failed: " + (e.response?.data?.error || e.message));
        }
    };

    return (
        <div className="workflow-details-page">
            <div className="workflow-header">
                <h3 className="workflow-title">
                    {data.short_name} <span style={{ fontSize: 13, color: "#0369a1" }}>· 🌐 Public pipeline (BYOP)</span>
                </h3>
                {data.github_url && (
                    <a href={data.github_url} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontSize: 13 }}>
                        {data.github_url}
                    </a>
                )}
            </div>

            <section className="workflow-section">
                <h4 className="workflow-section-title">Workflow Summary</h4>
                {data.workflow_summary ? (
                    <div className="workflow-summary">{data.workflow_summary}</div>
                ) : (
                    <p className="workflow-muted-text">No workflow summary available.</p>
                )}
            </section>


            <section className="workflow-section">
                <h4 className="workflow-section-title">Programs Used in Analysis</h4>
                {(() => {
                    const steps = normalizePrograms(data.containers_list);
                    if (!steps.length) return <p className="workflow-muted-text">No programs listed for this workflow.</p>;
                    return (
                        <table className="workflow-programs-table">
                            <thead>
                                <tr>
                                    <th className="workflow-programs-header-step">STEP</th>
                                    <th className="workflow-programs-header-programs">PROGRAMS USED</th>
                                </tr>
                            </thead>
                            <tbody>
                                {steps.map((step, i) => (
                                    <tr key={i}>
                                        <td className="workflow-programs-step-cell">{step.name}</td>
                                        <td className="workflow-programs-programs-cell">
                                            {step.programs.map((p, j) => (
                                                <div key={j} className="workflow-program-entry">
                                                    <strong>{p.name}</strong>
                                                    {p.version && ` (v${p.version})`}
                                                    {p.default_parameters && (
                                                        <div className="workflow-program-default-params">Default params: {p.default_parameters}</div>
                                                    )}
                                                </div>
                                            ))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    );
                })()}
            </section>

            {/* SOURCE CODE — view/edit/add/delete pipeline files */}
            <section className="workflow-section">
                <h4 className="workflow-section-title">Pipeline Source Code</h4>
                <p className="workflow-muted-text" style={{ marginTop: 0 }}>
                    Edit, add, or delete files — code edits take effect on the next launch. The container Dockerfile is separate;
                    editing it re-validates the pipeline (rebuilds the image). <code>nextflow.config</code> is platform-managed and locked.
                </p>
                {codeNotice && (
                    <div style={{ margin: "6px 0", padding: "8px 12px", borderRadius: 6, background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af", fontSize: 13 }}>
                        {codeNotice}
                    </div>
                )}
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <button className="upload-primary-button" onClick={addFile}>+ Add file</button>
                    <button className="upload-primary-button" onClick={loadAudit}>Change history</button>
                </div>
                {fileTree.filter((f) => f.type === "file").length === 0 ? (
                    <p className="workflow-muted-text">No source files on disk. (Older imports may not have downloaded the code.)</p>
                ) : (
                    <div style={{ maxHeight: 340, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 6, padding: 8 }}>
                        {renderTreeNode(buildFileTree(fileTree))}
                    </div>
                )}

                {auditRows && (
                    <div style={{ marginTop: 12, border: "1px solid #e2e8f0", borderRadius: 6, padding: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <strong style={{ fontSize: 13 }}>Change history ({auditRows.length})</strong>
                            <button style={linkBtn} onClick={() => setAuditRows(null)}>Hide</button>
                        </div>
                        {auditRows.length === 0 ? (
                            <p className="workflow-muted-text" style={{ margin: "6px 0 0" }}>No changes recorded yet.</p>
                        ) : (
                            <div style={{ maxHeight: 260, overflow: "auto" }}>
                                <table className="workflow-programs-table" style={{ margin: "6px 0 0" }}>
                                    <thead>
                                        <tr>
                                            <th className="workflow-programs-header-step">When</th>
                                            <th className="workflow-programs-header-programs">User</th>
                                            <th className="workflow-programs-header-programs">Action</th>
                                            <th className="workflow-programs-header-programs">File</th>
                                            <th className="workflow-programs-header-programs"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {auditRows.map((a) => (
                                            <tr key={a.id}>
                                                <td className="workflow-programs-step-cell" style={{ whiteSpace: "nowrap", fontSize: 12 }}>{new Date(a.created_at).toLocaleString()}</td>
                                                <td className="workflow-programs-programs-cell" style={{ fontSize: 12 }}>{a.user_email || a.user_sub || "—"}</td>
                                                <td className="workflow-programs-programs-cell" style={{ fontSize: 12 }}>{a.action}</td>
                                                <td className="workflow-programs-programs-cell" style={{ fontFamily: "monospace", fontSize: 11 }}>
                                                    {a.action === "rename" ? `${a.old_path} → ${a.file_path}` : (a.file_path || "—")}
                                                </td>
                                                <td className="workflow-programs-programs-cell">
                                                    {a.has_prev ? <button style={linkBtn} onClick={() => viewAuditEntry(a.id)}>View previous</button> : null}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </section>

            {/* PARAMETERS */}
            <section className="workflow-section">
                <h4 className="workflow-section-title">Set Parameters</h4>
                {params.length === 0 ? (
                    <p className="workflow-muted-text">This pipeline has no configurable parameters.</p>
                ) : (
                    <table className="workflow-programs-table">
                        <thead>
                            <tr>
                                <th className="workflow-programs-header-step">PARAMETER</th>
                                <th className="workflow-programs-header-programs">VALUE</th>
                            </tr>
                        </thead>
                        <tbody>
                            {params.map((p, i) => (
                                <tr
                                    key={i}
                                    style={showValidation && (missingParamNames.includes(p.name) || invalidParamNames.includes(p.name)) ? { background: "#fef2f2", outline: "2px solid #ef4444" } : undefined}
                                >
                                    <td className="workflow-programs-step-cell" style={{ verticalAlign: "top" }}>
                                        <div><code>{p.flag}</code>
                                            {showValidation && missingParamNames.includes(p.name) ? <span style={{ color: "#ef4444" }}> — required</span> : null}
                                            {showValidation && invalidParamNames.includes(p.name) ? <span style={{ color: "#ef4444" }}> — must be an http(s):// or s3:// path</span> : null}
                                        </div>
                                        <div style={{ fontSize: 11, color: "#64748b" }}>{p.type}</div>
                                        {p.description ? <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>{p.description}</div> : null}
                                    </td>
                                    <td className="workflow-programs-programs-cell">
                                        {renderParamField(p)}
                                        {p.default !== "" && p.default != null && (
                                            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>default: {String(p.default)}</div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {params.length > 0 && (
                    <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 12 }}>
                        <button className="btn-secondary" onClick={saveParamDefaults} disabled={savingParams}>
                            {savingParams ? "Saving…" : "Save as default values"}
                        </button>
                        <span className="workflow-muted-text" style={{ fontSize: 12 }}>
                            These values are used at launch. Saving stores them permanently as this pipeline's defaults.
                        </span>
                        {paramNotice && <span style={{ fontSize: 12, color: paramNotice.startsWith("✓") ? "#166534" : "#b91c1c" }}>{paramNotice}</span>}
                    </div>
                )}
            </section>

            {/* INPUT FILES — drag from the library below into each section */}
            <div className="workflow-upload-section">
                <h4 className="workflow-upload-heading">Assign input files</h4>
                <p className="workflow-muted-text" style={{ marginTop: 0 }}>
                    Drag files from your library (right) into each input section. Tick multiple files and drag any ticked row to move them all at once.
                </p>

                <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
                  {/* LEFT: input drop sections */}
                  <div style={{ flex: "1 1 50%", minWidth: 0 }}>
                {inputs.length === 0 ? (
                    <p className="workflow-muted-text">This pipeline requires no input files.</p>
                ) : (
                    inputs.map((input, i) => {
                        const files = assigned[input.name] || [];
                        return (
                            <div className="upload-section" key={i}>
                                <label className="upload-section-label">
                                    {input.name} <code>{input.flag}</code>
                                    {input.extensions?.length ? (
                                        <span className="workflow-muted-text"> &nbsp;({input.extensions.join(", ")})</span>
                                    ) : null}
                                </label>
                                {input.description ? (
                                    <p className="workflow-muted-text" style={{ margin: "2px 0 6px" }}>{input.description}</p>
                                ) : null}
                                <div
                                    className="upload-dropzone"
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => onSectionDrop(e, input.name)}
                                    style={{
                                        minHeight: 60,
                                        ...(showValidation && missingInputNames.includes(input.name)
                                            ? { border: "2px solid #ef4444", background: "#fef2f2" }
                                            : {}),
                                    }}
                                >
                                    {files.length === 0 ? (
                                        <p className="upload-dropzone-text">Drop files here from your library.</p>
                                    ) : (
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                            {files.map((f) => (
                                                <span
                                                    key={f.id}
                                                    style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#e0f2fe", color: "#0369a1", borderRadius: 12, padding: "2px 10px", fontSize: 12 }}
                                                >
                                                    {f.name}
                                                    <button
                                                        onClick={() => removeFile(input.name, f.id)}
                                                        style={{ border: "none", background: "transparent", cursor: "pointer", color: "#0369a1", fontWeight: "bold" }}
                                                        title="Remove"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}

                {/* Static "Data" input — present for EVERY pipeline, in addition to the
                    inputs detected at import. Files dropped here are copied into the run
                    folder (so a samplesheet / script can reference them by name), but are
                    NOT added to the run command. Optional — never blocks launch. */}
                <div className="upload-section">
                    <label className="upload-section-label">
                        Data <span className="workflow-muted-text">&nbsp;(raw data files — optional)</span>
                    </label>
                    <p className="workflow-muted-text" style={{ margin: "2px 0 6px" }}>
                        Drop any data files your pipeline or samplesheet references by name (e.g. FASTQ/BAM).
                        These are copied into the analysis folder alongside the pipeline so it can find them; they are not added to the run command.
                    </p>
                    <div
                        className="upload-dropzone"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => onSectionDrop(e, "__data__")}
                        style={{ minHeight: 60 }}
                    >
                        {(assigned["__data__"] || []).length === 0 ? (
                            <p className="upload-dropzone-text">Drop files here from your library.</p>
                        ) : (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                {(assigned["__data__"] || []).map((f) => (
                                    <span
                                        key={f.id}
                                        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#e0f2fe", color: "#0369a1", borderRadius: 12, padding: "2px 10px", fontSize: 12 }}
                                    >
                                        {f.name}
                                        <button
                                            onClick={() => removeFile("__data__", f.id)}
                                            style={{ border: "none", background: "transparent", cursor: "pointer", color: "#0369a1", fontWeight: "bold" }}
                                            title="Remove"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                  </div>

                  {/* RIGHT: sticky, x-scrollable file library (drag source) */}
                  <div
                    style={{
                        flex: "1 1 50%",
                        minWidth: 0,
                        position: "sticky",
                        top: 16,
                        maxHeight: "calc(100vh - 140px)",
                        overflowY: "auto",
                        border: "1px solid #e2e8f0",
                        borderRadius: 8,
                        padding: 12,
                        background: "#fff",
                    }}
                  >
                <h4 className="workflow-section-title" style={{ marginTop: 0 }}>Your files</h4>
                <input
                    type="text"
                    placeholder="Search files (name, project, path, date)…"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    style={{ padding: 8, fontSize: 14, width: "100%", boxSizing: "border-box", marginBottom: 10, border: "1px solid #cbd5e1", borderRadius: 4 }}
                />
                {userFiles.length === 0 ? (
                    <p className="workflow-muted-text">No files in your library yet. Import or upload data first, then it will appear here.</p>
                ) : (
                    <>
                        <div style={{ overflowX: "auto" }}>
                        <table className="workflow-programs-table" style={{ minWidth: 640 }}>
                            <thead>
                                <tr>
                                    <th className="workflow-programs-header-step" style={{ width: 32 }}>
                                        <input type="checkbox" checked={pageAllSelected} onChange={toggleSelectPage} title="Select all on this page" />
                                    </th>
                                    <th className="workflow-programs-header-step"><SortHeader title="Filename" k="name" /></th>
                                    <th className="workflow-programs-header-programs"><SortHeader title="Project" k="project" /></th>
                                    <th className="workflow-programs-header-programs"><SortHeader title="S3 Path" k="path" /></th>
                                    <th className="workflow-programs-header-programs"><SortHeader title="Created" k="created" /></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedFiles.map((f) => (
                                    <tr
                                        key={f.id}
                                        draggable
                                        onDragStart={(e) => onRowDragStart(e, f)}
                                        style={{ cursor: "grab", background: selected[f.id] ? "#e0f2fe" : undefined }}
                                        title="Drag into an input section (drags all ticked files if this row is ticked)"
                                    >
                                        <td className="workflow-programs-step-cell" style={{ width: 32 }}>
                                            <input type="checkbox" checked={!!selected[f.id]} onChange={() => toggleSelect(f.id)} />
                                        </td>
                                        <td className="workflow-programs-step-cell" style={{ whiteSpace: "nowrap" }}>⠿ {f.name}</td>
                                        <td className="workflow-programs-programs-cell">{f.project}</td>
                                        <td className="workflow-programs-programs-cell" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{f.path}</td>
                                        <td className="workflow-programs-programs-cell" style={{ whiteSpace: "nowrap" }}>{f.created}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                        <Pagination page={page} totalPages={totalPages} onChange={setCurrentPage} />
                        {Object.keys(selected).length > 0 && (
                            <p className="workflow-muted-text" style={{ marginTop: 6 }}>
                                {Object.keys(selected).length} file(s) selected — drag any ticked row to add them all.
                            </p>
                        )}
                    </>
                )}
                  </div>
                </div>{/* end side-by-side */}


                {!validated && (
                    <div
                        style={{
                            margin: "12px 0", padding: "10px 14px", borderRadius: 8, border: "1px solid",
                            borderColor: data.validation_status === "failed" ? "#fca5a5" : "#fcd34d",
                            background: data.validation_status === "failed" ? "#fef2f2" : "#fffbeb",
                            color: data.validation_status === "failed" ? "#b91c1c" : "#92400e",
                            fontSize: 14,
                        }}
                    >
                        {data.validation_status === "failed" ? (
                            <>
                                ⚠️ This pipeline failed automated Docker-build validation and cannot be launched.
                                {data.validation_error ? (
                                    <details style={{ marginTop: 6 }}>
                                        <summary style={{ cursor: "pointer" }}>Build error</summary>
                                        <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>{data.validation_error}</pre>
                                    </details>
                                ) : null}
                            </>
                        ) : (
                            "⏳ This pipeline's container is being validated (Docker build). Launching will be enabled once validation completes."
                        )}
                    </div>
                )}

                {showValidation && !canLaunch && (
                    <div
                        style={{
                            margin: "12px 0", padding: "10px 14px", borderRadius: 8,
                            border: "1px solid #fca5a5", background: "#fef2f2", color: "#b91c1c", fontSize: 14,
                        }}
                    >
                        <strong>Can't launch yet — please complete the highlighted item(s):</strong>
                        <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
                            {missingInputNames.map((n) => <li key={`i-${n}`}>Input file: <code>{n}</code> (drag at least one file into it)</li>)}
                            {missingParamNames.map((n) => <li key={`p-${n}`}>Parameter: <code>{n}</code> (enter a value)</li>)}
                            {invalidParamNames.map((n) => <li key={`v-${n}`}>Parameter: <code>{n}</code> (must be an http(s):// or s3:// path)</li>)}
                        </ul>
                    </div>
                )}

                {validated && (
                    <div style={{ margin: "12px 0", padding: "12px 14px", borderRadius: 8, border: "1px solid #bfdbfe", background: "#eff6ff" }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1e40af", marginBottom: 6 }}>
                            {canLaunch ? "Final command to be run" : "Command preview (updates as you fill in the fields)"}
                        </div>
                        <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 12.5, color: "#0f172a" }}>{previewCommand}</pre>
                        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 6 }}>Platform flags (config, work-dir, weblog, output) are added automatically at launch.</div>
                    </div>
                )}

                <div className="workflow-launch-container">
                    <button
                        onClick={openLaunch}
                        className="workflow-launch-button"
                        disabled={!validated}
                        title={!validated ? "Pipeline is still being validated and can't be launched yet." : undefined}
                    >
                        LAUNCH ANALYSIS
                    </button>
                </div>
            </div>

            <Modal
                isOpen={modalOpen}
                onRequestClose={() => setModalOpen(false)}
                className="workflow-modal"
                overlayClassName="workflow-modal-overlay"
                ariaHideApp={false}
            >
                <h2 className="workflow-modal-title">Launch Analysis</h2>
                {!launchResult ? (
                    <>
                        <p className="workflow-modal-body">
                            This will run the pipeline on the cloud with the selected files and parameters. Proceed?
                        </p>
                        <div className="workflow-modal-buttons">
                            <button onClick={confirmLaunch} className="workflow-modal-button workflow-modal-button-confirm">YES</button>
                            <button onClick={() => setModalOpen(false)} className="workflow-modal-button workflow-modal-button-cancel">NO</button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="workflow-modal-body">{launchResult}</p>
                        <div className="workflow-modal-buttons">
                            <button
                                onClick={() => { setModalOpen(false); setLaunchResult(""); }}
                                className="workflow-modal-button workflow-modal-button-cancel"
                            >
                                CLOSE
                            </button>
                        </div>
                    </>
                )}
            </Modal>

            {/* Source-code editor modal (textarea — no extra editor dependency) */}
            <Modal
                isOpen={!!editorFile}
                onRequestClose={() => setEditorFile(null)}
                ariaHideApp={false}
                style={{
                    overlay: { backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000 },
                    content: { inset: "4%", padding: 20, borderRadius: 10, display: "flex", flexDirection: "column" },
                }}
            >
                {editorFile && (
                    <>
                        <h2 style={{ fontFamily: "monospace", fontSize: 16, margin: "0 0 8px" }}>{editorFile.path}</h2>
                        {editorFile.isDockerfile && (
                            <p style={{ fontSize: 12, color: "#92400e", margin: "0 0 6px" }}>
                                Saving the container Dockerfile re-validates the pipeline (rebuilds the image) before it can be launched again.
                            </p>
                        )}
                        {editorFile.editable === false && (
                            <p style={{ fontSize: 12, color: "#b91c1c", margin: "0 0 6px" }}>This file is locked and cannot be edited.</p>
                        )}
                        <textarea
                            value={editorFile.content}
                            onChange={(e) => setEditorFile({ ...editorFile, content: e.target.value })}
                            readOnly={editorFile.editable === false}
                            spellCheck={false}
                            style={{ flex: 1, width: "100%", boxSizing: "border-box", fontFamily: "monospace", fontSize: 12, padding: 10, border: "1px solid #cbd5e1", borderRadius: 6, whiteSpace: "pre", overflow: "auto" }}
                        />
                        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 10 }}>
                            {editorFile.editable !== false && (
                                <button
                                    className="workflow-modal-button workflow-modal-button-confirm"
                                    disabled={editorSaving}
                                    onClick={saveEditorFile}
                                >
                                    {editorSaving ? "Saving…" : "SAVE"}
                                </button>
                            )}
                            <button className="workflow-modal-button workflow-modal-button-cancel" onClick={() => setEditorFile(null)}>
                                CLOSE
                            </button>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
};

const fieldStyle = { padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 4, minWidth: 220, fontSize: 13 };
const linkBtn = { border: "none", background: "transparent", color: "#2563eb", cursor: "pointer", fontSize: 12 };

// Build a nested tree { name: { __meta, __children } } from a flat path list.
function buildFileTree(flat) {
    const root = {};
    (flat || []).forEach((item) => {
        const parts = String(item.path).split("/").filter(Boolean);
        let node = root;
        parts.forEach((part, i) => {
            if (!node[part]) node[part] = { __meta: null, __children: {} };
            if (i === parts.length - 1) node[part].__meta = item;
            node = node[part].__children;
        });
    });
    return root;
}

export default BYOPWorkflow;
